import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';

import { stringify } from 'qs';
import dayjs from 'dayjs';
import { getLocalStorage, removeLocalStorage, setLocalStorage } from 'utils/browserStorageUtil';
import { ACCESS_TOKEN } from 'constant/token';
import AuthService from 'services/AuthService';
import { saveAs } from 'file-saver';
import {
  API_NOT_CHECK_UNAUTHORIZED,
  decodeJwt,
  isMatchPathUnauthorized,
} from 'utils/helperFunctions';
import store from 'configStore/storeRedux';

const domainUrl = `${process.env.REACT_APP_API_HOST}/api`;

const refreshTokenUrl = '/Authentication/login';
export type QueryObject = { [key: string]: string | number | boolean | null };
const authService = new AuthService();

// Add the 401 response interceptor
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const isSessionRedeem = store.getState().auth.isSessionRedeem;
    if (
      error?.response?.status === 401 &&
      (!error?.request?.responseURL || !isMatchPathUnauthorized(error?.request?.responseURL))
    ) {
      if (isSessionRedeem) {
        const accessToken = getLocalStorage(ACCESS_TOKEN);
        const expiredAt = accessToken ? decodeJwt(accessToken)?.exp : undefined;
        const isTokenExpired = expiredAt ? dayjs.unix(expiredAt).isBefore(dayjs()) : true;
        if (!isTokenExpired || !(window as any)?.flutter_inappwebview) {
          return Promise.reject(error);
        }
        (window as any).flutter_inappwebview.callHandler('handleTokenExpire');
      } else {
        const userInfo = await authService.getUser();
        if (!userInfo) {
          await authService.logIn();
        } else {
          await authService.logOut();
        }
      }
    } else {
      return Promise.reject(error);
    }
  }
);

export default class ApiClient {
  /**
   *
   * @param url
   * @param params
   */
  static async get(
    url: string,
    params: object,
    query?: undefined | { [key: string]: string | boolean | null } | string,
    customDomain?: string
  ): Promise<AxiosResponse> {
    let requestUrl = query ? `${url}?${stringify(query)}` : url;
    if (typeof query === 'string') {
      requestUrl = `${url}${query}`;
    }
    const response = await axios.get((customDomain || domainUrl) + requestUrl, {
      params,
      headers: await this.getHeaders(),
      data: {},
    });
    return response;
  }

  static async getCustomHeader(
    url: string,
    params: object,
    query?: undefined | { [key: string]: string | boolean | null } | string,
    customHeader?: any
  ): Promise<AxiosResponse> {
    let requestUrl = query ? `${url}?${stringify(query)}` : url;
    if (typeof query === 'string') {
      requestUrl = `${url}${query}`;
    }
    const header = await this.getHeaders();
    const response = await axios.get(domainUrl + requestUrl, {
      params,
      headers: { ...header, ...(customHeader || {}) },
      data: {},
    });
    return response;
  }

  static async getNoHeader(
    url: string,
    params: object,
    query?: undefined | { [key: string]: string | boolean | null } | string
  ): Promise<AxiosResponse> {
    let requestUrl = query ? `${url}?${stringify(query)}` : url;
    if (typeof query === 'string') {
      requestUrl = `${url}${query}`;
    }
    const response = await axios.get(domainUrl + requestUrl, {
      params,
      headers: {
        'Content-Type': 'application/json',
      },
      data: {},
    });
    return response;
  }

  static async getHeaders(contentType = 'application/x-www-form-urlencoded') {
    return {
      'Content-Type': contentType,
      authorization: await this.getToken(),
    };
  }

  private static async getToken() {
    const timeNow = dayjs();
    const accessToken = getLocalStorage(ACCESS_TOKEN);
    if (!accessToken) {
      return '';
    }
    //access token get from SSO Gate
    const expiredToken = decodeJwt(accessToken)?.exp;
    const isTokenExpired = dayjs.unix(expiredToken ?? 0).isBefore(timeNow);
    if (!isTokenExpired) {
      return `Bearer ${accessToken}`;
    } else {
      return '';
    }
  }

  private static convertToPostData(obj: any, form: any, namespace: any) {
    const fd = form || new URLSearchParams();
    let formKey;

    for (const property in obj) {
      if (obj.hasOwnProperty(property)) {
        if (namespace) {
          if (!isNaN(Number(property))) {
            formKey = `${namespace}[${property}]`;
          } else {
            formKey = `${namespace}.${property}`;
          }
        } else {
          formKey = property;
        }

        if (obj[property] instanceof Date) {
          fd.append(formKey, obj[property].toISOString());
        } else if (
          Array.isArray(obj[property]) &&
          obj[property].every((item: any) => item instanceof File)
        ) {
          const fileList = obj[property] as File[];
          for (const file of fileList) {
            fd.append(formKey, file);
          }
        } else if (
          typeof obj[property] === 'object' &&
          !(obj[property] instanceof File) &&
          !(obj[property] instanceof Blob)
        ) {
          this.convertToPostData(obj[property], fd, formKey);
        } else {
          fd.append(formKey, obj[property]);
        }
      }
    }

    return fd;
  }

  static async post(
    url: string,
    query: QueryObject,
    params: any,
    appendUrl?: string,
    customDomain?: string
  ): Promise<AxiosResponse> {
    const requestUrl = `${url}?${stringify(query)}${appendUrl || ''}`;

    const config: AxiosRequestConfig = {
      headers: await this.getHeaders(),
    };

    const param = this.convertToPostData(params, undefined, undefined);
    const response = await axios.post((customDomain || domainUrl) + requestUrl, param, config);
    return response;
  }

  private static async refreshToken(): Promise<string> {
    try {
      const refreshToken = getLocalStorage('accessRefreshToken');
      const config: AxiosRequestConfig = {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      };
      const response = await axios.post(
        domainUrl + refreshTokenUrl,
        stringify({ refreshToken }),
        config
      );
      setLocalStorage(
        'expiredToken',
        dayjs()
          .add(Math.floor(Number(response?.data?.expiresIn || 0) / 60), 'minutes')
          .format('YYYY-MM-DD HH:mm:ss')
      );
      setLocalStorage('accessToken', response?.data?.accessToken);
      setLocalStorage('accessRefreshToken', response?.data?.refreshToken);
      return response?.data?.accessToken || '';
    } catch (error) {
      removeLocalStorage(ACCESS_TOKEN);
      removeLocalStorage('accessRefreshToken');
      removeLocalStorage('expiredToken');
      removeLocalStorage('staffId');
      removeLocalStorage('user');
      return '';
    }
  }

  static async getJsonData(
    url: string,
    params: object,
    query?: undefined | { [key: string]: string | boolean } | string
  ): Promise<AxiosResponse> {
    let requestUrl = query ? `${url}?${stringify(query)}` : url;
    if (typeof query === 'string') {
      requestUrl = `${url}${query}`;
    }
    const response = await axios.get(domainUrl + requestUrl, {
      params,
      headers: await this.getHeaders('application/json'),
      data: {},
    });
    return response;
  }

  static async postJsonData(
    url: string,
    query: QueryObject,
    params: any,
    extraConfig?: Partial<AxiosRequestConfig>
  ): Promise<AxiosResponse> {
    const requestUrl = `${url}?${stringify(query)}`;

    const config: AxiosRequestConfig = {
      headers: await this.getHeaders('application/json'),
      ...extraConfig,
    };

    const response = await axios.post(domainUrl + requestUrl, params, config);
    return response;
  }

  static async postJsonDataNoHeader(
    url: string,
    query: QueryObject,
    params: any,
    extraConfig?: Partial<AxiosRequestConfig>
  ): Promise<AxiosResponse> {
    const requestUrl = `${url}?${stringify(query)}`;

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'application/json',
      },
      ...extraConfig,
    };

    const response = await axios.post(domainUrl + requestUrl, params, config);
    return response;
  }

  static async putJsonData(url: string, query: QueryObject, params: any): Promise<AxiosResponse> {
    const requestUrl = `${url}?${stringify(query)}`;

    const config: AxiosRequestConfig = {
      headers: await this.getHeaders('application/json'),
    };

    const response = await axios.put(domainUrl + requestUrl, params, config);
    return response;
  }

  static async delete(url: string, params: any): Promise<AxiosResponse> {
    const requestUrl = `${url}?${stringify(params)}`;
    const config: AxiosRequestConfig = {
      headers: await this.getHeaders('application/json'),
      // validateStatus,
    };
    const response = await axios.delete(domainUrl + requestUrl, config);
    return response;
  }

  static async postMutipartData(
    url: string,
    query: QueryObject,
    params: any
  ): Promise<AxiosResponse> {
    const requestUrl = `${url}?${stringify(query)}`;
    const config: AxiosRequestConfig = {
      headers: await this.getHeaders('multipart/form-data'),
    };
    const form = new FormData();
    const param = this.convertToPostData(params, form, undefined);

    const response = await axios.post(domainUrl + requestUrl, param, config);

    return response;
  }

  static async downloadExcelPost(
    url: string,
    query: undefined | { [key: string]: string | boolean } | string,
    params: object,
    fileName = 'excel_table',
    isFinished = true
  ): Promise<AxiosResponse> {
    let requestUrl = query ? `${url}?${stringify(query)}` : url;
    if (typeof query === 'string') {
      requestUrl = `${url}${query}`;
    }
    const config: AxiosRequestConfig = {
      headers: await this.getHeaders('application/json'),
      responseType: 'blob',
    };

    const response = await axios.post(domainUrl + requestUrl, params, config);
    if (isFinished) {
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, fileName);
    }
    return response;
  }

  static async downloadExcelGet(
    url: string,
    params: object,
    query?: undefined | { [key: string]: string | boolean } | string,
    fileName?: string
  ) {
    let requestUrl = query ? `${url}?${stringify(query)}` : url;
    if (typeof query === 'string') {
      requestUrl = `${url}${query}`;
    }
    const response = await axios.get(domainUrl + requestUrl, {
      params,
      headers: await this.getHeaders('application/xlsx'),
      responseType: 'blob',
      data: {},
    });

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, fileName);
    return response;
  }

  static async downloadExcelGetXlsm(
    url: string,
    params: object,
    query?: undefined | { [key: string]: string | boolean | number } | string,
    fileName?: string
  ) {
    let requestUrl = query ? `${url}?${stringify(query)}` : url;
    if (typeof query === 'string') {
      requestUrl = `${url}${query}`;
    }
    const response = await axios.get(domainUrl + requestUrl, {
      params,
      headers: await this.getHeaders('application/vnd.ms-excel'),
      responseType: 'blob',
      data: {},
    });

    const blob = new Blob([response.data], {
      type: 'application/vnd.ms-excel',
    });
    saveAs(blob, fileName);
    return response;
  }

  /** GET tải file dạng blob, **không** tự lưu — nơi gọi kiểm tra nội dung (file hay JSON lỗi) rồi mới lưu */
  static async getFile(url: string, params: object): Promise<AxiosResponse<Blob>> {
    const response = await axios.get(domainUrl + url, {
      params,
      headers: await this.getHeaders(),
      responseType: 'blob',
    });
    return response;
  }

  static async uploadFileExcelPostMutipartData(
    url: string,
    query: QueryObject,
    params: any,
    option?: any
  ): Promise<AxiosResponse> {
    const requestUrl = `${url}?${stringify(query)}`;
    const config: AxiosRequestConfig = {
      headers: await this.getHeaders('multipart/form-data'),
      onUploadProgress: option?.onUploadProgress,
    };
    // const form = new FormData();
    // const param = this.convertToPostData(params, form, undefined);
    const response = await axios.post(domainUrl + requestUrl, params, config);

    return response;
  }
}
